import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Box, Tabs, Tab, Button } from '@mui/material';
import { map } from 'lodash-es';
import GrowerYieldRawTab from './GrowerYieldRawTab';
import GrowerYieldCleanTab from './GrowerYieldCleanTab';
import GrowerYieldTabPanel from './GrowerYieldTabPanel';
import YearSelector from './YearSelector';
import ActionButton from '../../components/ActionButton';
import { DownloadButton } from '../../components/DownloadButton';
import { downloadCleanYieldCsv } from '../../services/yield-service';
import {
  downloadYieldPdfs,
  sendCropToYieldClean,
  reAssociateYield,
} from '../../lib/api';
import { selectors, actions } from '../../store/dux/yield-dux';
import { selectors as growerSelectors } from '../../store/dux/grower-dux';
import { usePermissions } from '../../hooks/use-permissions';
import { usePermission } from '../../hooks/use-permission';
import YieldUpdateCropDialog from './YieldCropUpdateDialog';
import YieldCalibrationDialog from './YieldCalibrationDialog';
import { useFields } from '../../hooks/use-fields';
import { YieldRemoval } from './YieldRemoval';
import { useContext } from './context.jsx';

export default function GrowerYieldGrid() {
  const dispatch = useDispatch();
  const permissions = usePermissions();
  const { selectedAaids } = useFields();
  const growerId = useSelector(growerSelectors.selectedId) || '';
  const loading = useSelector(selectors.loading);
  const cleanYield = useSelector(selectors.cleanYield);
  const rawYield = useSelector(selectors.rawYield);
  const [cropEditOpen, setCropEditOpen] = useState(false);
  const [calibrationOpen, setCalibrationOpen] = useState(false);
  const [isConfirmingRemoval, setConfirmingRemoval] = useState(false);
  const allowedToRemove = permissions.includes('ADMIN_YIELD');
  const allowedToClean = permissions.includes('CLEAN_YIELD');
  const showActions = usePermission('VIEW_ACTIONS');
  const { tab, setTab, year } = useContext();

  const selectedRawFields = selectedAaids
    .map((aaid) => rawYield?.find((raw) => aaid === raw.aaid))
    .filter(Boolean);

  useEffect(() => {
    if (year && growerId) dispatch(actions.loadYieldData(growerId, year));
  }, [year, growerId, dispatch]);

  const tabChanged = (_, newValue) => setTab(newValue);

  async function sendRawYieldToAssociate() {
    await reAssociateYield(growerId, year, map(selectedRawFields, 'aaid'));
    dispatch(actions.loadYieldData(growerId, year));
  }

  async function cleanFields() {
    await sendCropToYieldClean(
      growerId,
      year,
      selectedRawFields.map((field) => ({
        fieldName: field.fieldName,
        aaid: field.aaid,
        crop: field.harvestedCrop,
      })),
    );

    dispatch(actions.loadYieldData(growerId, year));
  }

  function canForceClean() {
    return (
      allowedToClean &&
      selectedRawFields.length &&
      selectedRawFields.every(
        (raw) => raw.status === 'Not enough coverage' && raw.crops?.length <= 1,
      )
    );
  }

  function canReprocess() {
    return (
      selectedRawFields.length &&
      selectedRawFields.every((raw) => !isApproved(raw))
    );
  }

  function canChangeCrop() {
    return (
      selectedRawFields.length &&
      selectedRawFields.every((raw) => hasYield(raw) && !isApproved(raw))
    );
  }

  const tabStyle = {
    minWidth: '180px',
    background: '#F8F8F8',
    boxShadow: '0px -1px 0px 0px #C2C2C2 inset',
    '&.Mui-selected': { backgroundColor: 'white' },
  };
  const toolbarStyle = { mt: 2, mr: 2, mb: 1.5 };

  return (
    <Box sx={{ width: '100%', height: '100%' }}>
      {isConfirmingRemoval ? (
        <YieldRemoval
          onClose={() => setConfirmingRemoval(false)}
          year={year}
          aaids={selectedAaids}
        />
      ) : null}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ mt: toolbarStyle.mt }}>
          <Tabs value={tab} onChange={tabChanged}>
            <Tab label="Clean Yield" value="clean" sx={tabStyle} />
            <Tab label="Raw Yield" value="raw" sx={tabStyle} />
          </Tabs>
        </Box>
        <Box>
          <YearSelector sx={toolbarStyle} />
          {showActions && buildActions()}
          {cropEditOpen && (
            <YieldUpdateCropDialog
              onClose={() => setCropEditOpen(!cropEditOpen)}
              selectedRawFields={selectedRawFields}
            />
          )}
          {calibrationOpen && (
            <YieldCalibrationDialog
              onClose={() => setCalibrationOpen(!calibrationOpen)}
              selectedRawFields={selectedRawFields}
            />
          )}
        </Box>
      </Box>
      <GrowerYieldTabPanel value={tab} id="clean">
        <GrowerYieldCleanTab />
      </GrowerYieldTabPanel>
      <GrowerYieldTabPanel value={tab} id="raw" sx={{ width: '100%' }}>
        <GrowerYieldRawTab />
      </GrowerYieldTabPanel>
    </Box>
  );

  function buildRemoveButton() {
    if (!allowedToRemove) return null;

    return (
      <Button
        disabled={!selectedAaids.length}
        onClick={() => setConfirmingRemoval(true)}
      >
        Remove Yield Data
      </Button>
    );
  }

  function buildActions() {
    return tab === 'raw' ? (
      <ActionButton sx={toolbarStyle}>
        {allowedToClean && (
          <Button disabled={!canForceClean()} onClick={() => cleanFields()}>
            Force Clean
          </Button>
        )}
        <Button
          disabled={!canReprocess()}
          onClick={() => sendRawYieldToAssociate()}
        >
          Reprocess
        </Button>
        <Button
          disabled={!canChangeCrop()}
          onClick={() => setCropEditOpen(true)}
        >
          Update Harvested Crop
        </Button>
        {buildRemoveButton()}
      </ActionButton>
    ) : (
      <ActionButton sx={toolbarStyle}>
        <DownloadButton
          disabled={!selectedAaids.length || loading}
          keep-open="true"
          variant="text"
          size="medium"
          download={() =>
            downloadCleanYieldCsv(growerId, year, cleanYield, selectedAaids)
          }
        >
          Export CSV
        </DownloadButton>
        <DownloadButton
          disabled={!selectedAaids.length || loading}
          keep-open="true"
          variant="text"
          size="medium"
          download={() => downloadYieldPdfs(growerId, year, selectedAaids)}
        >
          Print to PDF
        </DownloadButton>
        <Button
          disabled={!canReprocess()}
          onClick={() => setCalibrationOpen(true)}
        >
          Yield Calibration
        </Button>
        {buildRemoveButton()}
      </ActionButton>
    );
  }
}

function hasYield(row) {
  return !/no yield/i.test(row.status);
}

function isApproved(row) {
  return Boolean(row.yieldapproved);
}
